import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messageInput", "submitButton", "submitLabel", "eventTypeInput", "suggestedFollowupUsedInput", "commandShortcutIdInput"]

  static values = {
    enableNotes: Boolean,
  }

  connect() {
    // Bind the methods once and store references for cleanup
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    this.boundHandleKeyUp = this.handleKeyUp.bind(this)
    this.boundHandleBlur = this.handleBlur.bind(this)
    this.boundHandleKeyPress = this.handleKeyPress.bind(this)

    this.messageInputTarget.addEventListener('keydown', this.boundHandleKeyDown)
    this.messageInputTarget.addEventListener('keyup', this.boundHandleKeyUp)
    this.messageInputTarget.addEventListener('blur', this.boundHandleBlur)
    this.messageInputTarget.addEventListener('keypress', this.boundHandleKeyPress)
  }

  disconnect() {
    this.messageInputTarget.removeEventListener('keydown', this.boundHandleKeyDown)
    this.messageInputTarget.removeEventListener('keyup', this.boundHandleKeyUp)
    this.messageInputTarget.removeEventListener('blur', this.boundHandleBlur)
    this.messageInputTarget.removeEventListener('keypress', this.boundHandleKeyPress)
  }

  handleKeyDown(event) {
    if (event.altKey && this.enableNotesValue) {
      this.setSubmitMode("note")
    }

    // Handle ESC key to send "stop" command when textfield is empty and agent is working
    if (event.key === "Escape" && this.messageInputTarget.value.trim() === "") {
      // Check if there's a loader indicating the agent is working
      const loader = document.querySelector('[data-event-type="loading"]')
      if (loader) {
        event.preventDefault()
        this.stop()
      }
    }
  }

  // Stop the agent: the "stop" chat message.
  stop() {
    this.setMessage("stop")
    this.eventTypeInputTarget.value = "chat"
    this.submitButtonTarget.form.requestSubmit()
  }

  handleKeyUp(event) {
    if (!event.altKey && this.enableNotesValue) {
      this.setSubmitMode("send")
    }
  }

  handleBlur() {
    this.setSubmitMode("send")
    this.eventTypeInputTarget.value = "chat"
  }

  // Fill the field from code (a suggested follow-up, a command shortcut, the
  // ESC stop). Assigning .value fires no input event, so the growing textarea
  // would keep the height of whatever was there before — the box has to be told.
  setMessage(value) {
    this.messageInputTarget.value = value
    this.messageInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Say which of the two things the button will do. An <input type="submit">
  // says it in its label; the composer's round icon button has none, so it
  // swaps its glyph, its accessible name and its tint instead.
  setSubmitMode(mode) {
    const button = this.submitButtonTarget
    const isNote = mode === "note"

    if (button.tagName === "INPUT") {
      button.value = isNote ? "Note" : "Send"
      return
    }

    // Swap the button variant rather than trying to out-specify it: .btn-* lives
    // in the utilities layer, which outranks components, so a rule in the
    // composer's own stylesheet could never recolour this button.
    button.dataset.mode = mode
    button.classList.toggle("btn-primary-action", !isNote)
    button.classList.toggle("btn-secondary", isNote)
    button.title = isNote
      ? "Save as an internal note — kept on the workflow, never sent to the agent"
      : "Send instruction to agent"

    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isNote ? "Note" : "Send"
    }

    const icon = button.querySelector("i")
    if (!icon) return

    icon.classList.toggle("ti-note", isNote)
    icon.classList.toggle("ti-arrow-up", !isNote)
  }

  // The field is a textarea now, so Enter has two jobs and has to pick one.
  // Enter sends, Shift+Enter opens a line, Alt+Enter files a note. Every send
  // path preventDefault()s: without it the newline is inserted as well, and the
  // instruction posts with a stray blank line on the end.
  // isComposing guards an IME — mid-composition Enter commits the candidate and
  // must not send.
  handleKeyPress(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

    event.preventDefault()

    if (event.altKey && this.enableNotesValue) {
      this.eventTypeInputTarget.value = "admin_note"
    } else {
      this.eventTypeInputTarget.value = "chat"
    }

    this.submitButtonTarget.form.requestSubmit()
  }

  executeCommand(event) {
    console.log("agent-chat executeCommand called", event)
    const detail = event?.detail || {}
    console.log("Event detail:", detail)
    const message = (detail.message || "").toString().trim()
    console.log("Message:", message)
    if (!message) {
      console.log("No message, returning")
      return
    }

    if (this.hasMessageInputTarget) {
      console.log("Setting message input value")
      this.setMessage(message)
      this.messageInputTarget.focus()
    } else {
      console.log("No message input target")
    }

    if (this.hasEventTypeInputTarget) {
      console.log("Setting event type to chat")
      this.eventTypeInputTarget.value = "chat"
    }

    if (this.hasSuggestedFollowupUsedInputTarget) {
      this.suggestedFollowupUsedInputTarget.value = "false"
    }

    // Set command shortcut ID if provided
    if (this.hasCommandShortcutIdInputTarget) {
      const shortcutId = detail.command_shortcut_id || detail.commandShortcutId || ""
      console.log("Setting command shortcut ID:", shortcutId)
      this.commandShortcutIdInputTarget.value = shortcutId
    }

    const shouldSubmit = detail.submit !== false
    console.log("Should submit:", shouldSubmit)
    if (shouldSubmit && this.hasSubmitButtonTarget) {
      console.log("Submitting form")
      this.submitButtonTarget.form?.requestSubmit()
    } else {
      console.log("Not submitting - shouldSubmit:", shouldSubmit, "hasSubmitButtonTarget:", this.hasSubmitButtonTarget)
    }
  }

  followup(e) {
    const followupMessage = e.target.dataset.followupValue
    this.setMessage(followupMessage)
    this.messageInputTarget.focus()
    this.suggestedFollowupUsedInputTarget.value = "true"
    this.submitButtonTarget.form.requestSubmit()
  }

  setAsNote() {
    this.eventTypeInputTarget.value = "admin_note"
    this.submitButtonTarget.form.requestSubmit()
  }
}
